import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../services/api'; 

const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hover, setHover] = useState(false);
  
  // --- NEW: Reference for detecting clicks ---
  const dropdownRef = useRef(null); 

  useEffect(() => {
    const initialize = async () => {
      try {
        await api.post('/notifications/jobs/trigger-compliance');
        fetchNotifications();
      } catch (err) { console.error(err); }
    };
    initialize();
    const interval = setInterval(fetchNotifications, 30000); 
    return () => clearInterval(interval);
  }, []);

  // --- NEW: Handle Clicks Outside ---
  useEffect(() => {
    function handleClickOutside(event) {
      // If dropdown is open AND click is OUTSIDE the dropdown component
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false); // Close it
        // The click event continues to propagate, so it hits the background element!
      }
    }

    // Attach listener to the whole document
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Cleanup listener on unmount
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications/mine');
      if (res.data) {
        setNotifications(res.data.data || []);
        setUnreadCount(res.data.unreadCount || 0);
      }
    } catch (err) { console.error(err); }
  };

  const markAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) { console.error(err); }
  };

  const deleteNotification = async (e, id) => {
    e.stopPropagation(); 
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n._id !== id));
      setUnreadCount(prev => {
        const wasUnread = notifications.find(n => n._id === id)?.isRead === false;
        return wasUnread ? Math.max(0, prev - 1) : prev;
      });
    } catch (err) {
      console.error('Failed to delete', err);
    }
  };

  return (
    // Attach the ref here so we know what is "inside"
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '40px', height: '40px',
          border: hover || isOpen ? '1px solid #2D7DD2' : '1px solid #E5E7EB',
          borderRadius: '6px',
          backgroundColor: hover || isOpen ? 'rgba(45, 125, 210, 0.05)' : '#FFFFFF',
          color: '#2D7DD2', cursor: 'pointer', position: 'relative', transition: 'all 0.2s ease',
        }}
      >
        <Bell size={18} strokeWidth={2} />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: '6px', right: '6px', width: '8px', height: '8px',
            backgroundColor: '#ef4444', borderRadius: '50%', border: '2px solid #ffffff',
          }} />
        )}
      </button>

      {isOpen && (
        <div style={{
            position: 'absolute', top: '100%', right: 0, marginTop: '8px', width: '320px',
            backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)', zIndex: 1000, overflow: 'hidden',
          }}
        >
          <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1F2937' }}>Notifications</h3>
            <button onClick={() => setIsOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}><X size={16} /></button>
          </div>

          <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>No new notifications</div>
            ) : (
              notifications.map((note) => (
                <div
                  key={note._id}
                  style={{
                    padding: '12px 16px', borderBottom: '1px solid #f3f4f6', position: 'relative',
                    backgroundColor: note.isRead ? '#FFFFFF' : '#F0F9FF',
                    cursor: 'pointer', transition: 'background 0.2s'
                  }}
                  onClick={() => markAsRead(note._id)}
                >
                  <button 
                    onClick={(e) => deleteNotification(e, note._id)}
                    style={{
                      position: 'absolute', top: '12px', right: '12px',
                      background: 'transparent', border: 'none', cursor: 'pointer', color: '#9CA3AF',
                      zIndex: 10 // Ensure this sits above the card click
                    }}
                    title="Remove"
                  >
                    <X size={14} />
                  </button>

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', paddingRight: '20px' }}>
                    <span style={{ 
                      fontSize: '11px', fontWeight: 'bold', padding: '2px 8px', borderRadius: '10px',
                      backgroundColor: note.type === 'error' ? '#FEE2E2' : '#E0F2FE',
                      color: note.type === 'error' ? '#B91C1C' : '#0369A1'
                    }}>
                      {note.type === 'error' ? 'Escalation' : 'Reminder'}
                    </span>
                    <span style={{ fontSize: '11px', color: '#9CA3AF' }}>
                      {new Date(note.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#1F2937', marginBottom: '2px', paddingRight: '15px' }}>{note.title}</div>
                  <div style={{ fontSize: '13px', color: '#4B5563', lineHeight: '1.4' }}>{note.message}</div>
                  
                  {note.link && (
                    <Link to="/contracts/obligations" onClick={() => setIsOpen(false)} style={{ display: 'block', marginTop: '6px', fontSize: '12px', color: '#2563EB', textDecoration: 'none' }}>
                      View Details →
                    </Link>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
      
      {/* REMOVED: The fixed overlay div is gone. 
          Now, if you click the background, the click passes through 
          and the useEffect closes the menu. */}
    </div>
  );
};

export default NotificationBell;